/**
 * Returns the canonical public site URL for share-card-style downloads
 * where the link must work for whoever scans the QR — i.e. always
 * production, never `localhost:3000` or a Netlify preview hostname.
 *
 * Order of precedence:
 *  1. NEXT_PUBLIC_PUBLIC_SITE_URL (override; intended for use on Netlify
 *     preview deploys where NEXT_PUBLIC_SITE_URL is the preview URL but
 *     we still want share cards to point at production)
 *  2. NEXT_PUBLIC_SITE_URL when it's an https origin
 *  3. https://easytiger.vn (production fallback)
 *
 * Trailing slash is stripped.
 */
export function getPublicSiteUrl(): string {
  const override = process.env.NEXT_PUBLIC_PUBLIC_SITE_URL;
  const configured = process.env.NEXT_PUBLIC_SITE_URL;
  const candidate = override || (configured && /^https:\/\//.test(configured) ? configured : "");
  const url = candidate || "https://easytiger.vn";
  return url.replace(/\/$/, "");
}

export function getPublicProfileUrl(slug: string): string {
  return `${getPublicSiteUrl()}/professionals/${slug}`;
}

/**
 * Returns the public origin (scheme + host) of the incoming request, even
 * when Netlify's Next.js runtime rewrites `request.url` to the deploy-
 * specific hostname (`<hash>--easytiger.netlify.app`).
 *
 * Use this in server route handlers when constructing absolute redirect
 * URLs — `new URL(request.url).origin` returns the internal deploy URL
 * on Netlify, which silently breaks OAuth callback redirects (cookie
 * gets set on the prod host but the user lands on the deploy preview).
 *
 * Header precedence:
 *  1. x-forwarded-host / x-forwarded-proto — set by Netlify's edge to the
 *     original client-visible host (easytiger.vn) and scheme.
 *  2. host header — set by the browser to whatever it requested.
 *  3. `request.url` origin — last-resort fallback when no proxy headers
 *     are present (e.g. direct hits in local dev).
 */
export function getRequestOrigin(request: Request): string {
  // x-forwarded-host / x-forwarded-proto can be comma-separated when a
  // request traverses multiple proxies (e.g. if someone ever puts Cloudflare
  // in front of Netlify): RFC 7239 convention says the leftmost entry is
  // the original client-visible value; subsequent entries were appended by
  // each hop. We want the original, so take the first.
  const firstValue = (header: string | null): string | null => {
    if (!header) return null;
    const first = header.split(",")[0]?.trim();
    return first || null;
  };

  const fallback = new URL(request.url);
  const proto =
    firstValue(request.headers.get("x-forwarded-proto")) ??
    fallback.protocol.replace(/:$/, "");
  // The standard `host` header is singular per RFC 7230 — no comma-split needed.
  const host =
    firstValue(request.headers.get("x-forwarded-host")) ??
    request.headers.get("host") ??
    fallback.host;
  return `${proto}://${host}`;
}
